import type { AiCapabilityPage } from "../schema.js";
import { LIVE, ALL_ROLES } from "./_shared.js";

/**
 * `EXP-VOTE` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId` (ADR-0009 bất biến 5).
 * 🔴 US-AIV2-0110 S10 (`@UC0259`): CỐ Ý còn trỏ trang chủ nhóm — **app chưa có màn**: màn bỏ phiếu `/vote/:proposalId`
 * cần expenseId (engine chỉ điền `groupId`) và không màn/tab nào liệt kê khoản chi đang chờ bỏ phiếu. Nợ của PO; ghi ở
 * `_appHasNoScreenYet` (`apps/mobile/test/features/ai_chat/presentation/ai_redirect_screen_routes_test.dart`).
 * Cửa mobile trang này chịu trách nhiệm (phiếu đồng thuận có `weightSnapshot` đóng băng lúc mở phiên ⇒ bấm trên màn phiếu, không qua chat).
 */
export const EXP_VOTE = {
  id: "EXP-VOTE",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: ALL_ROLES,
  flags: [],
  slots: [],
  llm: {
    /*
     * 🔴 US-AIV2-0110 S11 (@UC0273) — hai câu `known-intent-gaps` loại "bịa ô" (đo 24/09): câu HỎI *"có phiếu nào chờ tôi bỏ
     * phiếu không"* ⇒ model chọn trang này (conf 0,86/0,74) và bịa `{"slot":"bỏ phiếu"}` cho một trang 0 ô. Trang đúng là
     * READ-PEOPLE-MINE (chỉ số `việc chờ tôi` — hộp việc chờ gồm phiếu chưa bỏ). Ranh giới đặt ở ĐÂY vì
     * `read-people-mine.ts` đang được làn S21 (trang "ai nợ ai") sửa song song.
     */
    description:
      "Bỏ phiếu đồng ý hay không đồng ý cho một khoản chi cần cả nhóm quyết. Trang này là ĐI bỏ phiếu. Câu chỉ HỎI xem mình còn phiếu nào đang chờ không (\"có phiếu nào chờ tôi bỏ phiếu không\" / \"co phieu nao cho toi bo phieu khong\") là ĐỌC việc đang chờ mình, thuộc READ-PEOPLE-MINE.",
    examples: [
      "bỏ phiếu khoản chi",
      "bo phieu khoan chi",
      "tôi đồng ý khoản này",
      "toi dong y khoan nay",
      "biểu quyết",
      "vote",
      // ——— 0105: thêm cho đủ 40 ———
      "bieu quyet",
      "bỏ phiếu",
      "bo phieu",
      "tôi bỏ phiếu đồng ý",
      "toi bo phieu dong y",
      "tôi bỏ phiếu không đồng ý",
      "toi bo phieu khong dong y",
      "phiếu của tôi cho khoản chi này",
      "phieu cua toi cho khoan chi nay",
      "cho tôi biểu quyết khoản chi",
      "cho toi bieu quyet khoan chi",
      "mở phiếu biểu quyết",
      "mo phieu bieu quyet",
      "bỏ phiếu cho khoản chi cả nhóm quyết",
      "bo phieu cho khoan chi ca nhom quyet",
      "tôi muốn vote khoản chi",
      "toi muon vote khoan chi",
      "vào bỏ phiếu",
      "vao bo phieu",
      "biểu quyết khoản chi kiểu gì?",
      "bieu quyet khoan chi kieu gi",
      "phiếu biểu quyết của nhóm",
      "phieu bieu quyet cua nhom",
      "tôi chưa bỏ phiếu khoản nào",
      "toi chua bo phieu khoan nao",
      "vote cho khoản chi liên hoan",
      "vote cho khoan chi lien hoan",
      "cả nhóm đang biểu quyết cái gì vậy",
      "ca nhom dang bieu quyet cai gi vay",
      "mình muốn bỏ phiếu phản đối khoản chi này",
      "minh muon bo phieu phan doi khoan chi nay",
      "gửi phiếu của tôi",
      "gui phieu cua toi",
      "tôi bỏ phiếu thuận",
    ],
  },
  screen: "/groups/:groupId",
  screenShows: "group_activity_feed",
  screenLabel: "Xem hoạt động nhóm",
  endpoints: [
    "POST /expenses/{expenseId}/votes",
    // QĐ người dùng 12/10/2026 (@UC0035) — nhóm biểu quyết: quản lý MỞ biểu quyết cho yêu cầu chi ở màn YÊU CẦU CHI (GUI). Chat không
    // làm việc này ⇒ khai ở trang redirect về chuyện biểu quyết, không ở trang chat T2 `EXP-APPROVE` (sẽ đếm sai "làm được trong chat").
    "POST /expenses/{expenseId}/open-vote",
    // US-AIV2-0112 CONSENSUS-MOBILE (@UC0357): màn bỏ phiếu đọc lá phiếu của người xem TRƯỚC khi hiện nút (ẩn nút khi không còn phiếu).
    "GET /expenses/{expenseId}/vote-status",
  ],
  facts: [],
  templates: {
    // US-AIV2-tourguide-0008 (QĐ PO 05/10): app chưa có chỗ liệt kê khoản chi chờ bỏ phiếu (màn `/vote/:id` cần id khoản chi, không lối vào) ⇒ nói thật.
    // 🔴 AI-SO-DRIFT-1012 (@UC0035 · QĐ người dùng 12/10/2026): trang giữ CẢ cửa bỏ phiếu lẫn `open-vote` (người duyệt mở biểu quyết ở màn YÊU CẦU
    // CHI) ⇒ câu phủ cả hai. Lối thật: dòng yêu cầu chi ở tab Hoạt động ⇒ màn yêu cầu chi ⇒ «Đến biểu quyết» (`request.voteOpen`) / «Mở biểu quyết»
    // (`canOpenVote`). Vẫn chưa có chỗ liệt kê RIÊNG khoản chờ bỏ phiếu.
    redirect:
      "Trong app chưa có chỗ liệt kê riêng khoản chi đang chờ bỏ phiếu — mỗi khoản là một dòng yêu cầu chi ở tab «Hoạt động» của nhóm. Bấm vào dòng đó: đang biểu quyết thì có nút «Đến biểu quyết» để bỏ phiếu; nhóm quyết bằng biểu quyết mà chưa mở thì người duyệt khoản chi thấy nút «Mở biểu quyết». Phiếu của bạn ghi lại tên — cả nhóm nhìn thấy ai đã bỏ phiếu gì. Mình mở màn nhóm cho bạn nhé.",
  },
  journey: { after: ["READ-FUND-MONEY"] },
  help: { title: "Bỏ phiếu khoản chi", tryNow: "bỏ phiếu khoản chi" },
} satisfies AiCapabilityPage;
